import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import CompanySearchSelect from "../companies/CompanySearchSelect";
import SearchSelect from "../SearchSelect";
import { toast } from "sonner";

const SOURCES = [
    ["linkedin", "LinkedIn"], ["whatsapp", "WhatsApp"], ["referido", "Referido"],
    ["web", "Web"], ["llamada", "Llamada"], ["evento", "Evento"], ["email", "Email"],
    ["instagram", "Instagram"], ["facebook", "Facebook"], ["tiktok", "TikTok"], ["otros", "Otros"],
];
const USER_SOURCE_VALUES = new Set(SOURCES.map(([value]) => value));
const emptyForm = { first_name:"", last_name:"", email:"", phone:"", position:"", city:"", company_id:"", source:"", source_other:"", owner_id:"" };

export default function ContactFormDialog({ open, onOpenChange, contact = null, onSaved, users = [], canAssignOwner = false, canCreateCompany = true, currentUserId = "" }) {
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        const rawSource = contact?.source || "";
        const sourceIsUserOption = !rawSource || USER_SOURCE_VALUES.has(rawSource);
        setForm({
            ...emptyForm,
            ...(contact || {}),
            source: sourceIsUserOption ? rawSource : "otros",
            source_other: sourceIsUserOption ? (contact?.source_other || "") : (contact?.source_other || rawSource),
            owner_id: contact?.owner_id || currentUserId || "",
        });
    }, [open, contact, currentUserId]);

    const save = async () => {
        if (!form.first_name.trim()) { toast.error("Nombre obligatorio"); return; }
        if (!form.company_id) { toast.error("Selecciona una empresa existente"); return; }
        if (form.source === "otros" && !form.source_other.trim()) { toast.error("Especifica el origen del contacto"); return; }
        setSaving(true);
        try {
            const payload = { ...form, first_name: form.first_name.trim(), source_other: form.source === "otros" ? form.source_other.trim() : null };
            if (!canAssignOwner) delete payload.owner_id;
            const { data } = contact?.lead_id
                ? await api.patch(`/leads/${contact.lead_id}`, payload)
                : await api.post("/leads", payload);
            toast.success(contact ? "Contacto actualizado" : "Contacto creado");
            onOpenChange(false);
            onSaved?.(data);
        } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{contact ? "Editar" : "Nuevo"} contacto</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label>Nombre *</Label><Input value={form.first_name} onChange={(e)=>setForm({...form,first_name:e.target.value})} data-testid="lead-form-first-name"/></div>
                    <div><Label>Apellido</Label><Input value={form.last_name} onChange={(e)=>setForm({...form,last_name:e.target.value})} data-testid="lead-form-last-name"/></div>
                    <div><Label>Correo</Label><Input value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="lead-form-email"/></div>
                    <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="lead-form-phone"/></div>
                    <div><Label>Cargo</Label><Input value={form.position} onChange={(e)=>setForm({...form,position:e.target.value})} data-testid="lead-form-position"/></div>
                    <div><Label>Ciudad</Label><Input value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})}/></div>
                    <div className="col-span-2"><Label>Empresa *</Label><CompanySearchSelect value={form.company_id} onChange={(company_id)=>setForm({...form,company_id})} canCreate={canCreateCompany} data-testid="lead-form-company"/></div>
                    <div><Label>Origen</Label><Select value={form.source || ""} onValueChange={(source)=>setForm({...form,source,source_other:source === "otros" ? form.source_other : ""})}><SelectTrigger data-testid="lead-form-source"><SelectValue placeholder="Selecciona origen"/></SelectTrigger><SelectContent>{SOURCES.map(([value,label])=><SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select></div>
                    {form.source === "otros" && <div><Label>Otro origen *</Label><Input value={form.source_other} onChange={(e)=>setForm({...form,source_other:e.target.value})} placeholder="Ej. Evento, recomendación..." data-testid="lead-form-source-other"/></div>}
                    {canAssignOwner && <div className="col-span-2"><Label>Propietario</Label><SearchSelect value={form.owner_id} onChange={(owner_id)=>setForm({...form,owner_id})} options={users.map((u)=>({value:u.user_id,label:u.email?`${u.name} · ${u.email}`:u.name,keywords:u.email||""}))} allowClear={false} placeholder="Selecciona propietario" data-testid="lead-form-owner"/></div>}
                </div>
                <DialogFooter><Button variant="ghost" onClick={()=>onOpenChange(false)}>Cancelar</Button><Button onClick={save} disabled={saving} data-testid="lead-form-save">{saving ? "Guardando…" : "Guardar"}</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
